// Interface for the Google Tag Manager and dataLayer
interface Window {
  dataLayer?: any[];
}

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

// Use environment variable for GTM ID
const GTM_ID = import.meta.env.VITE_GTM_ID || 'GTM-PPJRZT7C';

/**
 * Service to manage cookie consent and interact with Google Tag Manager
 * for Cognitive Bias Lab website.
 * 
 * IMPORTANT: This service is designed to work with a pre-configured GTM setup
 * that already has consent mode enabled and appropriate triggers configured.
 * 
 * NOTE: As of the latest update, default consent is now set in index.html
 * before GTM loads, as required by Consent Mode v2.
 */
export class ConsentService {
  // Constants for event names - these should match the events expected by GTM
  private static readonly CONSENT_DEFAULT_EVENT = 'default_consent'; // Standard GTM consent event for Consent Mode v2
  private static readonly CONSENT_UPDATE_EVENT = 'consent_update';   // Standard GTM consent event
  private static readonly CONSENT_MANAGE_EVENT = 'cookie_consent_manage'; // Custom event for tracking consent management
  
  // Storage keys
  private static readonly CONSENT_STATUS_KEY = 'cookieConsent';
  private static readonly CONSENT_DATE_KEY = 'cookieConsentDate';
  
  // Debug mode - set to false to disable logging
  private static readonly DEBUG = false;

  /**
   * Ensures that gtag function is available (creates fallback if not)
   * to avoid errors when calling consent APIs
   */
  private static ensureGtagAvailable(): void {
    // Initialize dataLayer if it doesn't exist
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    // Create a fallback gtag function if it doesn't exist
    if (typeof window.gtag !== 'function') {
      this.debugLog('Creating fallback gtag function');
      window.gtag = function(...args: any[]) {
        // Ensure dataLayer exists before pushing
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(args);
      };
    }
  }

  /**
   * Initialize Google Tag Manager with default consent settings
   * This is kept for backwards compatibility but no longer sets the initial default consent,
   * as that's now done in index.html before GTM loads.
   * 
   * It now only handles applying any previously stored consent decisions.
   */
  static initializeConsent(): void {
    this.ensureGtagAvailable();
    this.debugLog('Checking for stored consent decisions');
    
    // Default consent is now in index.html - we don't need to set it here.
    // Just check for and apply previous consent choices.
    
    // Check if user previously accepted cookies
    const consentStatus = localStorage.getItem(this.CONSENT_STATUS_KEY);
    
    if (consentStatus === 'accepted') {
      this.debugLog('Found previous consent: accepted - updating GTM');
      this.acceptCookies('stored');
    } else if (consentStatus === 'rejected') {
      this.debugLog('Found previous consent: rejected - maintaining denied state');
      // No need to call rejectCookies() as default is already denied
      // But we should push a dataLayer event for analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': this.CONSENT_UPDATE_EVENT,
          'consent_status': 'rejected',
          'consent_source': 'stored'
        });
      }
    }
  }

  /**
   * Called when user accepts cookies - grants all consent types
   * @param source Optional parameter to track where the user accepted from (banner or manage modal)
   */
  static acceptCookies(source: 'banner' | 'manage_modal' | 'stored' | 'unknown' = 'unknown'): void {
    this.ensureGtagAvailable();
    this.debugLog(`Updating consent state to: granted (from: ${source})`);
    
    // Save to localStorage
    localStorage.setItem(this.CONSENT_STATUS_KEY, 'accepted');
    localStorage.setItem(this.CONSENT_DATE_KEY, new Date().toISOString());
    
    // First, update consent state
    window.gtag && window.gtag('consent', 'update', {
      'analytics_storage': 'granted'
    });
    
    // Then explicitly send config command to GA4
    window.gtag && window.gtag('config', 'G-D8C2PZFVX8', {
      'send_page_view': true
    });
    
    // Push event for GTM triggers
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': this.CONSENT_UPDATE_EVENT,
        'consent_status': 'accepted',
        'consent_source': source,
        'analytics_storage': 'granted'
      });
    }
    
    // Track consent management event if from manage modal
    if (source === 'manage_modal') {
      this.trackConsentManagement('accept');
    }
  }
  
  /**
   * Called when user rejects cookies - denies all consent types
   * @param source Optional parameter to track where the user rejected from (banner or manage modal)
   */
  static rejectCookies(source: 'banner' | 'manage_modal' | 'stored' | 'unknown' = 'unknown'): void {
    this.ensureGtagAvailable();
    this.debugLog(`Updating consent state to: denied (from: ${source})`);
    
    // Save to localStorage
    localStorage.setItem(this.CONSENT_STATUS_KEY, 'rejected');
    localStorage.setItem(this.CONSENT_DATE_KEY, new Date().toISOString());
    
    // Update consent state using gtag API
    window.gtag && window.gtag('consent', 'update', {
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied'
    });
    
    // Push separate event to dataLayer for GTM triggers and internal analytics
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': this.CONSENT_UPDATE_EVENT,
        'consent_status': 'rejected',
        'consent_source': source
      });
    }
    
    // Track consent management event if from manage modal
    if (source === 'manage_modal') {
      this.trackConsentManagement('reject');
    }
  }
  
  /**
   * Track when users interact with the consent management modal
   * This is useful for understanding how users manage their privacy preferences
   */
  static trackConsentManagement(action: 'open' | 'accept' | 'reject' | 'close'): void {
    this.ensureGtagAvailable();
    this.debugLog(`Tracking consent management action: ${action}`);
    
    // Push event to dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': this.CONSENT_MANAGE_EVENT,
        'cookie_consent_action': action,
        'consent_status': this.hasAcceptedCookies() ? 'accepted' : 'rejected'
      });
    }
  }
  
  /**
   * Check if the user has made a consent choice
   * @returns boolean indicating if a choice has been made
   */
  static hasConsentDecision(): boolean {
    return localStorage.getItem(this.CONSENT_STATUS_KEY) !== null;
  }
  
  /**
   * Check if the user has accepted cookies
   * @returns boolean indicating if cookies were accepted
   */
  static hasAcceptedCookies(): boolean {
    return localStorage.getItem(this.CONSENT_STATUS_KEY) === 'accepted';
  }
  
  /**
   * Clear the stored consent decision
   * Useful for testing or when privacy policy changes significantly
   */
  static resetConsent(): void {
    localStorage.removeItem(this.CONSENT_STATUS_KEY);
    localStorage.removeItem(this.CONSENT_DATE_KEY);
    this.debugLog('Consent reset - cleared from localStorage');
    
    // Re-initialize to default denied state
    window.gtag && window.gtag('consent', 'update', {
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied'
    });
    
    // Push event to dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': this.CONSENT_UPDATE_EVENT,
        'consent_status': 'reset',
        'consent_source': 'reset_function'
      });
    }
  }
  
  /**
   * Check how long ago the user made their consent decision
   * @returns number of days since consent was given, or null if no consent
   */
  static daysSinceConsentDecision(): number | null {
    const consentDate = localStorage.getItem(this.CONSENT_DATE_KEY);
    if (!consentDate) return null;
    
    const givenDate = new Date(consentDate);
    const now = new Date();
    const diffTime = now.getTime() - givenDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }
  
  /**
   * Internal method for logging debug messages
   * Only outputs if DEBUG is true
   */
  private static debugLog(message: string): void {
    if (this.DEBUG) {
      console.log(`[ConsentService] ${message}`);
    }
  }
} 